class CartFeeManager {
    constructor() {
        this.FEE_VARIANTS = {
            NYS: "41706733142129",
            FPS: "41706733305969",
            FPSSCION: "43135816269963"
        };

        this.ROYALTY_VARIANTS = {
            "Ambulo Blanc": {
                in: "43686656704651",
                out: "43135815057547"
            },
            "Ambulo Blanc Test": {
                in: "43686656704651",
                out: "43135815057547"
            },
            "Arandell": {
                in: "33333333330003",
                out: "33333333330004"
            },
            "Aromella": {
                in: "33333333330005",
                out: "33333333330006"
            },
            "Example Product": {
                in: "42229191999601",
                out: "42229193244785"
            }
        };

        this.isProcessing = false;
        this.lastProcessedHash = "";
        this.processingTimeout = null;
        this.cartData = null;
        this.retryCount = 0;
        this.maxRetries = 3;

        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupCartMonitoring());
        } else {
            this.setupCartMonitoring();
        }
    }

    setupCartMonitoring() {
        this.fetchAndProcessCart();
        this.setupEventListeners();
        this.setupPeriodicCheck();
    }

    setupEventListeners() {
        const cartForm = document.querySelector('form[action="/cart"]');
        if (cartForm) {
            cartForm.addEventListener('change', (e) => {
                if (e.target.name?.includes('updates[')) {
                    this.debouncedProcessCart();
                }
            });
        }

        document.addEventListener('click', (e) => {
            if (e.target.matches('.cart__qty-btn, .quantity-btn, [data-quantity-change]')) {
                this.debouncedProcessCart();
            }
        });

        document.addEventListener('click', (e) => {
            if (e.target.matches('.cart__remove, .remove-item, [href*="/cart/change"]')) {
                this.debouncedProcessCart();
            }
        });

        document.addEventListener('cart:updated', () => {
            this.debouncedProcessCart();
        });

        document.addEventListener('cart:refresh-fees', () => {
            this.debouncedProcessCart();
        });
    }

    setupPeriodicCheck() {
        setInterval(() => {
            if (!this.isProcessing) {
                this.fetchAndProcessCart();
            }
        }, 2000);
    }

    debouncedProcessCart() {
        if (this.processingTimeout) {
            clearTimeout(this.processingTimeout);
        }

        this.processingTimeout = setTimeout(() => {
            this.fetchAndProcessCart();
        }, 300);
    }

    async fetchCart() {
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Failed to fetch cart:', error);
            throw error;
        }
    }

    createCartHash(cartData) {
        if (!cartData?.items) return '';

        const relevantItems = cartData.items
            .filter(item => {
                const productType = item.product_type?.toLowerCase() || '';
                const variantId = item.variant_id.toString();

                const isFeeVariant = Object.values(this.FEE_VARIANTS).includes(variantId);
                const isRoyaltyVariant = Object.values(this.ROYALTY_VARIANTS).some(variant =>
                    Object.values(variant).includes(variantId)
                );
                const triggersRequirements = productType.includes('nys') ||
                    productType.includes('fps') ||
                    productType.includes('fpsscion') ||
                    productType.includes('royalty');

                return isFeeVariant || isRoyaltyVariant || triggersRequirements;
            })
            .map(item => ({
                id: item.variant_id,
                quantity: item.quantity,
                product_type: item.product_type,
                title: item.title,
                properties: item.properties
            }))
            .sort((a, b) => a.id - b.id);

        return JSON.stringify(relevantItems);
    }

    analyzeCartRequirements(cartData) {
        const analysis = {
            regularProducts: [],
            feeItems: [],
            royaltyItems: [],
            requiredFees: { NYS: 0, FPS: 0, FPSSCION: 0 },
            requiredRoyalties: {}
        };

        if (!cartData?.items) return analysis;

        try {
            for (const item of cartData.items) {
                const variantId = item.variant_id.toString();

                if (Object.values(this.FEE_VARIANTS).includes(variantId)) {
                    analysis.feeItems.push(item);
                } else if (Object.values(this.ROYALTY_VARIANTS).some(variant =>
                    Object.values(variant).includes(variantId)
                )) {
                    analysis.royaltyItems.push(item);
                } else {
                    analysis.regularProducts.push(item);
                }
            }

            for (const item of analysis.regularProducts) {
                const productType = item.product_type?.toLowerCase() || '';
                const productTypes = productType.split(',').map(s => s.trim()).filter(Boolean);
                const quantity = Math.max(0, item.quantity);

                if (productTypes.includes('nys')) {
                    analysis.requiredFees.NYS += quantity;
                }
                if (productTypes.includes('fps')) {
                    analysis.requiredFees.FPS += quantity;
                }
                if (productTypes.includes('fpsscion')) {
                    analysis.requiredFees.FPSSCION += quantity;
                }

                if (productTypes.includes('royalty')) {
                    const variantTitle = item.title?.trim() || '';
                    const grapeName = variantTitle.split('-')[0]?.trim();

                    if (grapeName && this.ROYALTY_VARIANTS[grapeName]) {
                        const royaltyProperty = item.properties?.royalty ||
                            item.options?.find(opt => opt.toLowerCase().includes('royalty'));

                        const royaltyType = royaltyProperty?.toLowerCase().includes('out') ? 'out' : 'in';
                        const targetVariantId = this.ROYALTY_VARIANTS[grapeName][royaltyType];

                        if (targetVariantId) {
                            analysis.requiredRoyalties[targetVariantId] =
                                (analysis.requiredRoyalties[targetVariantId] || 0) + quantity;
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error analyzing cart requirements:', error);
        }

        return analysis;
    }

    async updateCartItem(variantId, quantity) {
        try {
            const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    updates: {
                        [variantId]: quantity
                    }
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to update cart: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating cart item:', error);
            throw error;
        }
    }

    async addCartItem(variantId, quantity) {
        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    items: [{
                        id: variantId,
                        quantity: quantity
                    }]
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to add to cart: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error adding cart item:', error);
            throw error;
        }
    }

    async applyChanges(analysis) {
        const operations = [];

        try {
            for (const [feeType, requiredQty] of Object.entries(analysis.requiredFees)) {
                const variantId = this.FEE_VARIANTS[feeType];
                const existingItem = analysis.feeItems.find(item =>
                    item.variant_id.toString() === variantId
                );
                const currentQty = existingItem?.quantity || 0;

                if (requiredQty !== currentQty) {
                    operations.push({
                        type: requiredQty === 0 ? 'remove' : (existingItem ? 'update' : 'add'),
                        variantId: variantId,
                        quantity: requiredQty,
                        currentQuantity: currentQty
                    });
                }
            }

            for (const [variantId, requiredQty] of Object.entries(analysis.requiredRoyalties)) {
                const existingItem = analysis.royaltyItems.find(item =>
                    item.variant_id.toString() === variantId
                );
                const currentQty = existingItem?.quantity || 0;

                if (requiredQty !== currentQty) {
                    operations.push({
                        type: requiredQty === 0 ? 'remove' : (existingItem ? 'update' : 'add'),
                        variantId: variantId,
                        quantity: requiredQty,
                        currentQuantity: currentQty
                    });
                }
            }

            for (const item of analysis.royaltyItems) {
                const variantId = item.variant_id.toString();
                if (!analysis.requiredRoyalties[variantId]) {
                    operations.push({
                        type: 'remove',
                        variantId: variantId,
                        quantity: 0,
                        currentQuantity: item.quantity
                    });
                }
            }

            if (operations.length > 0) {
                await this.executeOperations(operations);

                this.triggerCartUpdate();
            }

        } catch (error) {
            console.error('Error applying changes:', error);
            throw error;
        }
    }

    async executeOperations(operations) {
        for (const op of operations) {
            try {
                if (op.type === 'add') {
                    await this.addCartItem(op.variantId, op.quantity);
                } else {
                    await this.updateCartItem(op.variantId, op.quantity);
                }

                await new Promise(resolve => setTimeout(resolve, 100));

            } catch (error) {
                console.error(`Failed to ${op.type} variant ${op.variantId}:`, error);
            }
        }
    }

    triggerCartUpdate() {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:fees-updated'));

        if (typeof Shopify !== 'undefined' && Shopify.theme?.sections?.load) {
            Shopify.theme.sections.load('cart');
        }

        if (window.theme && typeof window.theme.cartDrawer?.open === 'function') {
            window.theme.cartDrawer.refresh?.();
        }
    }

    async fetchAndProcessCart() {
        if (this.isProcessing) return;

        try {
            this.isProcessing = true;

            const cartData = await this.fetchCart();
            const currentHash = this.createCartHash(cartData);

            if (currentHash === this.lastProcessedHash) {
                return;
            }

            const analysis = this.analyzeCartRequirements(cartData);
            await this.applyChanges(analysis);

            this.lastProcessedHash = currentHash;
            this.retryCount = 0;

        } catch (error) {
            console.error('Error processing cart:', error);

            if (this.retryCount < this.maxRetries) {
                this.retryCount++;
                setTimeout(() => {
                    this.isProcessing = false;
                    this.fetchAndProcessCart();
                }, 1000 * this.retryCount);
            } else {
                console.error('Max retries exceeded for cart processing');
                this.retryCount = 0;
            }
        } finally {
            this.isProcessing = false;
        }
    }

    refresh() {
        this.lastProcessedHash = '';
        this.fetchAndProcessCart();
    }

    destroy() {
        if (this.processingTimeout) {
            clearTimeout(this.processingTimeout);
        }
        this.isProcessing = false;
    }
}

let cartFeeManager;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        cartFeeManager = new CartFeeManager();
    });
} else {
    cartFeeManager = new CartFeeManager();
}

window.CartFeeManager = CartFeeManager;
window.cartFeeManager = cartFeeManager;

if (typeof module !== 'undefined' && module.exports) {
    module.exports = CartFeeManager;
}